import React, { useEffect, useState } from 'react';
import type { User, PathwayProgress } from '../../types';
import { DataService } from '../../services/firebase';
import { useTranslation } from 'react-i18next';
import { Network, ChevronRight, Clock } from 'lucide-react';

interface MyPathwaysProps {
  user: User;
  onSelectPathway: (pathway: PathwayProgress) => void;
}

export const MyPathways: React.FC<MyPathwaysProps> = ({ user, onSelectPathway }) => {
  const { t } = useTranslation();
  const [pathways, setPathways] = useState<PathwayProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await DataService.getPathwaysForCandidate(user.id);
        setPathways(data);
      } catch (e) {
        console.error('Failed to load pathways', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  if (pathways.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 px-6 text-center">
        <div className="w-16 h-16 bg-aero-100 rounded-full flex items-center justify-center mb-4">
          <Network className="w-8 h-8 text-aero-400" />
        </div>
        <h3 className="text-lg font-semibold text-aero-900 mb-2">{t('candidate.myPathways.empty')}</h3>
        <p className="text-sm text-aero-500">{t('candidate.myPathways.emptyDesc')}</p>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    NOT_STARTED: 'bg-aero-100 text-aero-600',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-green-100 text-green-700',
  };

  return (
    <div className="p-4 space-y-3 max-w-2xl mx-auto">
      <h2 className="text-lg font-bold text-aero-900 mb-4">{t('candidate.myPathways.title')}</h2>
      {pathways.map((pathway) => (
        <button
          key={pathway.pathwayId}
          onClick={() => onSelectPathway(pathway)}
          className="w-full bg-white rounded-xl border border-aero-200 p-4 text-left hover:border-aero-400 hover:shadow-md transition-all group"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${statusColors[pathway.status] || statusColors.NOT_STARTED}`}>
                  {t(`candidate.myPathways.status.${pathway.status}`)}
                </span>
              </div>
              <h3 className="text-base font-semibold text-aero-900 truncate">{pathway.pathwayTitle}</h3>
              <div className="flex items-center gap-1 mt-1 text-xs text-aero-400">
                <Clock className="w-3 h-3" />
                <span>{t('candidate.myPathways.assignedOn')} {new Date(pathway.assignedDate).toLocaleDateString()}</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-aero-300 group-hover:text-aero-600 flex-shrink-0 mt-1 transition-colors" />
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-aero-500 mb-1">
              <span>{t('candidate.myPathways.progress')}</span>
              <span>{pathway.progress}%</span>
            </div>
            <div className="h-1.5 bg-aero-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${pathway.progress}%` }}
              />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};
